import { SigninUI } from 'features';
import { ForgotPassword } from '../forgot-password';
import { useEffect } from 'react';
import { useLogin, useFetchUser, useToast } from 'hooks';
import { Loader } from 'components';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Routes } from 'router';
import { resetStore } from 'state';
import { useAppDispatch } from 'state/hooks';
import { preservePendingRole } from 'utils/pendingHiringIntent';

/**
 * Interface for login payload
 */
export interface LoginPayload {
  email: string;
  password: string;
}

/**
 * Interface for login response
 */
export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  user: {
    id: number;
    email: string;
    phone_number: string | null;
    first_name: string | null;
    last_name: string | null;
    date_joined: string;
    country_code: string;
    date_of_birth: string | null;
    display_photo: string | null;
    address: string | null;
    user_type: string;
    objective: string;
    referral: string | null;
    company_info: any | null;
  };
}

const Signin = () => {
  // Hooks
  const { openToast } = useToast();
  const navigate = useNavigate();
  const { getUser } = useFetchUser();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const { mutate: runLogin, data: loginResponse, isPending: loginPending, error: loginError } = useLogin();

  useEffect(() => {
    if (localStorage.getItem('logout')) {
      // Keep the landing-page "what do you want to assess?" intent across this
      // reset — it is the only thing here that belongs to the *incoming* visit
      // rather than the signed-out session, and dropping it is what stopped the
      // "Pick Up Where You Left Off" modal from appearing on the log-in route.
      preservePendingRole(() => localStorage.clear());
      dispatch(resetStore());
      queryClient.clear();
    }
  }, [dispatch, queryClient]);

  const handleLogin = (data: LoginPayload) => {
    localStorage.removeItem('precisionAccess');
    localStorage.removeItem('precisionRefresh');
    localStorage.removeItem('precisionId');
    queryClient.clear();
    runLogin(data);
  };

  // Handle successful login
  useEffect(() => {
    if (loginResponse) {
      // Store tokens immediately
      localStorage.setItem('precisionAccess', loginResponse.access_token);
      localStorage.setItem('precisionRefresh', loginResponse.refresh_token);
      localStorage.setItem('precisionId', String(loginResponse.user.id));

      const user = loginResponse.user;

      const incompleteProfile =
        user.first_name === null ||
        user.last_name === null ||
        user.referral === null ||
        user.phone_number === null;

      const incompleteBusiness = user.company_info === null;

      // Fetch user data and plans
      getUser();

      // Show toast
      openToast({
        show: true,
        heading: 'Login successful!',
        text: 'Redirecting you to our Hiring Assistant...',
        type: 'success',
        timeOut: 1500,
      });

      // Navigate immediately
      navigate(
        `${Routes.publishAssessment}${
          incompleteProfile
            ? '?onboarding=true&incomplete=personal'
            : incompleteBusiness
            ? '?onboarding=true&incomplete=business'
            : ''
        }`,
        { replace: true }
      );
    }
  }, [loginResponse]);

  // Handle login error
  useEffect(() => {
    if (loginError) {
      const error = (loginError as any)?.response?.data;
      openToast({
        show: true,
        heading: 'Sorry',
        text: Array.isArray(error?.detail) ? error.detail[0] : 'Failed to login, please try again later',
        type: 'error',
        timeOut: 5000,
      });
    }
  }, [loginError]);

  const showLoader = loginPending;

  return (
    <>
      <Loader loading={showLoader} />
      <SigninUI
        sumbit={handleLogin}
        handleForgot={() => navigate(Routes.forgotPassword)}
      />
    </>
  );
};

export { Signin };